import ClearIcon from "@mui/icons-material/Clear";
import {
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
} from "@mui/material";
import {
  CalendarPicker,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { DateTime, Interval } from "luxon";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { colors } from "../../helpers/colors";

type CalendarFocus = "from" | "until" | null;

type DateRangePickerProps = {
  id?: string;
  label?: string;
  from: string | null;
  setFrom: (s: string | null) => void;
  until: string | null;
  setUntil: (s: string | null) => void;
};

export default function DateRangePicker(props: DateRangePickerProps) {
  const [focus, setFocus] = useState<CalendarFocus>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleClick = useCallback((calendar: CalendarFocus) => {
    const el = document.getElementById(`from_date_${props.id || ""}`);
    if (el) setAnchorEl(el);
    setFocus(calendar);
  }, []);
  const open = useMemo(() => !!anchorEl, [anchorEl]);
  useEffect(() => {
    if (!open) setFocus(null);
  }, [open]);

  return (
    <Stack direction="row" spacing={0.5} alignItems={"center"}>
      <div>
        <DateTextField
          id={`from_date_${props.id || ""}`}
          label={props.label || "From"}
          value={props.from}
          setValue={props.setFrom}
          onClick={() => handleClick("from")}
          focused={focus === "from"}
        />
      </div>
      <div>-</div>
      <div>
        <DateTextField
          id={`until_date_${props.id || ""}`}
          label={props.label ? "" : "Until"}
          value={props.until || ""}
          setValue={props.setUntil}
          onClick={() => handleClick("until")}
          focused={focus === "until"}
        />
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Calendar
          from={props.from}
          setFrom={props.setFrom}
          until={props.until}
          setUntil={props.setUntil}
          closePopover={() => setAnchorEl(null)}
          focus={focus}
          setFocus={setFocus}
        />
      </Popover>
    </Stack>
  );
}

type DateTextFieldProps = {
  id: string;
  label: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  focused: boolean;
  value: string | null;
  setValue: (v: string | null) => void;
};

function DateTextField(props: DateTextFieldProps) {
  return (
    <TextField
      id={props.id}
      style={{
        backgroundColor: "white",
        marginTop: "0px",
        width: "146px",
        marginLeft: "0px",
      }}
      size="small"
      label={props.label}
      value={props.value || ""}
      onChange={(event) => props.setValue(event.target.value)}
      onClick={props.onClick}
      focused={props.focused}
      InputProps={{
        endAdornment: props.value && (
          <InputAdornment position="end">
            <IconButton edge="end" onClick={() => props.setValue(null)}>
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

function Calendar(props: {
  from: string | null;
  setFrom: (v: string) => void;
  until: string | null;
  setUntil: (v: string | null) => void;
  closePopover: () => void;
  focus: CalendarFocus;
  setFocus: Dispatch<SetStateAction<CalendarFocus>>;
}) {
  return (
    <CalendarPicker
      disableHighlightToday
      date={DateTime.fromFormat(
        props.from || DateTime.now().toFormat("yyyy-MM-dd"),
        "yyyy-MM-dd"
      )}
      onChange={dateChange}
      renderDay={renderDays}
    />
  );

  function dateChange(newDate: DateTime | null) {
    // No date selected, do nothing
    if (!newDate) return;

    if (props.focus === "from") {
      props.setFrom(newDate.toFormat("yyyy-MM-dd"));
      if (
        props.until &&
        DateTime.fromFormat(props.until, "yyyy-MM-dd") < newDate
      )
        props.setUntil(null);
      props.setFocus("until");
    } else if (props.focus === "until") {
      if (props.from && DateTime.fromFormat(props.from, "yyyy-MM-dd") > newDate)
        return;
      props.setUntil(newDate.toFormat("yyyy-MM-dd"));
      props.closePopover();
      props.setFocus(null);
    }
  }

  function renderDays(
    date: DateTime,
    _selectedDates: Array<DateTime | null>,
    pickersDayProps: PickersDayProps<DateTime>
  ) {
    const dateString = date.toFormat("yyyy-MM-dd");

    // If no until set, only select first day
    if (!props.until || props.from === props.until)
      return (
        <PickersDay {...pickersDayProps} selected={dateString === props.from} />
      );

    // Get all dates that should be selected
    const datesThatShouldBeSelected = props.from
      ? Interval.fromDateTimes(
          DateTime.fromFormat(props.from, "yyyy-MM-dd").startOf("day"),
          DateTime.fromFormat(props.until, "yyyy-MM-dd").endOf("day")
        )
          .splitBy({ day: 1 })
          .map((d) => d.start?.toFormat("yyyy-MM-dd"))
      : [];

    // Set styling
    let style: React.CSSProperties = {};
    if (
      datesThatShouldBeSelected.includes(dateString) &&
      ![props.from, props.until].includes(dateString)
    ) {
      // A date between, thats not first or last date
      style = {
        ...style,
        backgroundColor: colors.brightNavyBlue,
        color: "white",
        borderRadius: "0%",
      };
    }
    if (dateString === props.from)
      style = {
        ...style,
        backgroundColor: colors.brightNavyBlue,
        borderTopLeftRadius: "50%",
        borderBottomLeftRadius: "50%",
        borderTopRightRadius: "0%",
        borderBottomRightRadius: "0%",
      };
    if (dateString === props.until)
      style = {
        ...style,
        backgroundColor: colors.brightNavyBlue,
        borderTopLeftRadius: "0%",
        borderBottomLeftRadius: "0%",
        borderTopRightRadius: "50%",
        borderBottomRightRadius: "50%",
      };

    return (
      <PickersDay
        {...pickersDayProps}
        selected={[props.from, props.until].includes(dateString)}
        style={style}
      />
    );
  }
}
